import React, {useContext, useEffect, useRef, useState} from 'react';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import Logout from '@mui/icons-material/Logout';
import {AccountCircle} from "@mui/icons-material";
import {useAuth} from "../../../hooks/useAuth";
import {useNavigate} from "react-router-dom";
import {styled} from "@mui/material/styles";
import MuiAppBar from "@mui/material/AppBar";
import {AppBarProps as MuiAppBarProps} from "@mui/material/AppBar/AppBar";
import {routeConfigs} from "../../../navigation/routeConfig";
import {IUser} from "../../../queries/user/userModel";
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import {Badge, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, Drawer} from "@mui/material";
import {contextObject} from "../../shared/ShoppingCart/ShoppingCartContext";
import ShoppingCart from "../../shared/ShoppingCart/ShoppingCart";
import Link from "@mui/material/Link";
import Box from "@mui/material/Box";
import Tooltip from '@mui/material/Tooltip';

import logoSrc from './logo.svg';
import { useMutationDeleteAllCart } from '../../../queries/product/hooks/useMutationDeleteAllCart';
import { CART } from '../../../queries/product/productTypeDefs';
import { useQueryUserSavings } from '../../../queries/user/hooks/useQueryUserSavings';
import { currencyFormat } from '../../utils/utils';

const drawerWidth: number = 300;
interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const StyledBadge = styled(Badge)({
  "& .MuiBadge-badge": {
    color: "rgba(0, 0, 0, 0.87)",
    backgroundColor: '#CCCCCC',
    fontWeight: 'bold'
  }
});

interface IHeaderProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  user: IUser;
  pharmacy: any;
  isAdmin: boolean;
}
export const Header:React.FunctionComponent<IHeaderProps> = ({
  open,
  setOpen,
  user,
  pharmacy,
  isAdmin
}) => {
  const { signOut } = useAuth();
  const navigate  = useNavigate();
  const [cartOpen, setCartOpen] = useState<boolean>(false);
  const {cart, loading} = useContext(contextObject);
  const [openCartNotification, setOpenCartNotification] = useState<boolean>(false);
  const timer = useRef<any>(null);
  const [deleteAllCartItems, { deleteLoading }] = useMutationDeleteAllCart({
    refetchQueries: [
      {
        query: CART,
        variables: {
          where: { userId: { equals: +user.id } },
          orderBy: { createdAt: 'asc' }
        },
        awaitRefetchQueries: true,
      },
    ],
  });
  
  const accountName = isAdmin ? `${user.firstName} ${user.lastName}` : pharmacy.legalName;
  const { data: savingsData, loading: savingsLoading } = useQueryUserSavings({
    fetchPolicy: 'cache-and-network',
    skip: isAdmin,
  });
  const savingsAmount: number = savingsData?.savingsWithTradeNetRx ?? 0;
  const vacationIsActive = Boolean(user?.vacationIsActive);

  const toggleDrawer = () => {
    setOpen(!open);
  };

  const toggleDrawerCart = (anchor: any, open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
    if (
      event.type === 'keydown' &&
      ((event as React.KeyboardEvent).key === 'Tab' ||
        (event as React.KeyboardEvent).key === 'Shift')
    ) {
      return;
    }

    setCartOpen(!cartOpen);
  };

  useEffect(() => {
    if ( timer.current ) {
      clearTimeout(timer.current);
    }
    if (cart.length > 0) {
      timer.current = setTimeout(() => setOpenCartNotification(true), 3600000);
    }
    return () => {
      timer.current && clearTimeout(timer.current);
    };
  }, [cart])

  const refreshTimeout = () => {
    setOpenCartNotification(false);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setOpenCartNotification(true), 10 * 1000);
  };

  const emptyCart = () => {
    deleteAllCartItems({variables: { where: { userId: {equals: +user.id} } }}).then(() => {
      setOpenCartNotification(false);
    });
  }

  return (
    <AppBar position="absolute" open={open}>
      <Toolbar sx={{ pr: '24px', display: 'flex', justifyContent: 'space-between', bgcolor: '#090808', color: '#FFFEFE' }}>
        <Box sx={{display: 'flex', alignItems: 'center'}}>
          <IconButton
            edge="start"
            color="inherit"
            aria-label="open drawer"
            onClick={toggleDrawer}
            sx={{ marginRight: '36px', ...(open && { display: 'none' }) }}
          >
            <MenuIcon />
          </IconButton>
          <Link href={routeConfigs.buy.path} sx={{color: '#FFFEFE'}} variant="body2" >
            <img height={56} src={logoSrc} alt='logo' />
          </Link>
        </Box>
        <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'end'}}>
          {!isAdmin && (
            <Tooltip
              title="Free Priority Overnight shipping on orders $3,000+"
              arrow
            >
              <Box
                sx={{
                  mr: 3,
                  textAlign: 'center',
                  lineHeight: 1.2,
                  cursor: 'help',
                }}
              >
                <Box sx={{ fontSize: 12, opacity: 0.85 }}>Free Priority Shipping:</Box>
                <Box sx={{ fontWeight: 'bold', fontSize: 14 }}>On $3,000+</Box>
              </Box>
            </Tooltip>
          )}
          {!isAdmin && (
            <Box
              sx={{
                width: '1px',
                height: 28,
                bgcolor: 'rgba(255, 255, 255, 0.35)',
                mr: 3,
              }}
            />
          )}
          {!isAdmin && (
            <Box
              sx={{
                mr: 3,
                cursor: 'pointer',
                textAlign: 'center',
                lineHeight: 1.2,
              }}
              onClick={() => navigate(routeConfigs.vacationMode.path)}
            >
              <Box sx={{ fontSize: 12, opacity: 0.85 }}>Vacation mode:</Box>
              <Box
                sx={{
                  fontWeight: 'bold',
                  color: vacationIsActive ? '#d32f2f' : '#2e7d32',
                }}
              >
                {vacationIsActive ? 'Active' : 'Inactive'}
              </Box>
            </Box>
          )}
          {!isAdmin && (
            <Box
              sx={{
                width: '1px',
                height: 28,
                bgcolor: 'rgba(255, 255, 255, 0.35)',
                mr: 3,
              }}
            />
          )}
          {!isAdmin && (
            <>
              <Box
                sx={{ display: 'flex', alignItems: 'center', mr: 3, cursor: 'pointer' }}
                onClick={() => navigate(routeConfigs.buyHistory.path)}
              >
                <AccountBalanceWalletIcon sx={{ mr: 1 }} />
                {savingsLoading ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ fontSize: 12, opacity: 0.8 }}>Saved on BUY Orders</Box>
                    <Box sx={{ fontWeight: 'bold', fontSize: 14 }}>
                      {currencyFormat(savingsAmount)}
                    </Box>
                  </Box>
                )}
              </Box>
              <StyledBadge
                badgeContent={cart.length}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                sx={{ ml: 5 }}
              >
                {(loading || deleteLoading) ? (
                  <CircularProgress color="inherit" />
                ) : (
                  <ShoppingCartIcon
                    sx={{ cursor: 'pointer' }}
                    onClick={cart.length > 0 ? toggleDrawerCart('right', true) : undefined}
                  />
                )}
              </StyledBadge>
              <Drawer
                anchor='right'
                open={cartOpen}
                onClose={toggleDrawerCart('right', false)}
                PaperProps={{
                  sx: { width: "100%", maxWidth: 400, },
                }}
              >
                <ShoppingCart setCartOpen={setCartOpen} />
              </Drawer>
              {openCartNotification && (
                <Dialog
                  fullWidth
                  maxWidth='sm'
                  open={Boolean(openCartNotification)}
                  onClose={refreshTimeout}
                  aria-labelledby="alert-dialog-title"
                  aria-describedby="alert-dialog-description"
                >
                  <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                      Are you still interested in purchasing this item?
                    </DialogContentText>
                  </DialogContent>
                  <DialogActions>
                    <Button
                      variant='outlined'
                      onClick={emptyCart}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant='contained'
                      onClick={refreshTimeout}
                    >
                      Buy
                    </Button>
                  </DialogActions>
                </Dialog>
              )}
            </>
          )}
          <Box sx={{display: 'flex', alignItems: 'center', cursor: 'pointer', mr: 2, ml: 3}} onClick={() => navigate(routeConfigs.profile.path)}>
            <AccountCircle />
            <Box sx={{ ml: 1, fontSize: 14 }}>{accountName}</Box>
          </Box>
          <IconButton
            color="inherit"
            onClick={async () => {
              await signOut();
              navigate ('/login');
            }}
          >
            <Logout />
            <Box sx={{ml: 0.5, fontSize: 14}}>Logout</Box>
          </IconButton>
        </Box>
      </Toolbar>
    </AppBar>
  )
}

export default Header;